// src/pages/UsersSettings.js
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  FiChevronDown,
  FiEdit2,
  FiMail,
  FiPhone,
  FiPlus,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";

import axios from "../axiosConfig";
import "../styles/UsersSettings.css";

import {
  useUsersSettingsData,
} from "../hooks/useUsersSettingsData";

import {
  normalizeMiniUser,
} from "../utils/usersSettingsCache";

import UserDetailsModal from "../components/UserDetailsModal";
import CreateUserModalPage from "./CreateUserModalPage";

const ROLE_KEYS = [
  "admin",
  "manager",
  "dispatcher",
  "accountant"
];

const STATUS_KEYS = ["active", "disabled"];

const ACTIVE_ROLE_KEY = "users.activeRoleTab";

const getInitialRole =
  () =>
    localStorage.getItem(
      ACTIVE_ROLE_KEY
    ) ||
    "admin";

export default function UsersSettings() {
  const { t } =
    useTranslation("users_settings");

  const initialRole =
    getInitialRole();

  const [
    role,
    setRole,
  ] = useState(
    initialRole
  );

  const {
    users,
    loading,
    error:
      usersError,

    patchUsers:
      updateUsersAndCache,
  } =
    useUsersSettingsData(
      role
    );


  const error =
    usersError
      ? t(
          "errors.load_failed"
        )
      : "";


  const [
    showLoading,
    setShowLoading,
  ] = useState(false);

  const [
    query,
    setQuery,
  ] = useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");

  const [
    confirming,
    setConfirming,
  ] = useState(null);

  const [
    isDetailsModalOpen,
    setIsDetailsModalOpen,
  ] = useState(false);

  const [
    selectedUserId,
    setSelectedUserId,
  ] = useState(null);

  const [
    isCreateModalOpen,
    setIsCreateModalOpen,
  ] = useState(false);

  const roleOptions =
    useMemo(
      () =>
        ROLE_KEYS.map(
          (key) => ({
            value: key,
            label: t(
              `roles.${key}`
            ),
          })
        ),
      [t]
    );

  const activeRoleIndex =
    useMemo(
      () =>
        Math.max(
          0,
          roleOptions.findIndex(
            (option) =>
              option.value === role
          )
        ),
      [
        role,
        roleOptions,
      ]
    );

  const currentRoleLabel =
    t(`roles.${role}`);

  useEffect(() => {
    localStorage.setItem(
      ACTIVE_ROLE_KEY,
      role
    );
  }, [role]);

  useEffect(() => {
    if (!loading) {
      setShowLoading(
        false
      );

      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          setShowLoading(
            true
          );
        },
        300
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [loading]);

  const filteredUsers =
    useMemo(
      () => {
        const normalizedQuery =
          query
            .trim()
            .toLowerCase();

        return users.filter(
          (user) => {
            const status =
              String(
                user.status ||
                  "active"
              ).toLowerCase();

            if (
              statusFilter !==
                "all" &&
              status !==
                statusFilter
            ) {
              return false;
            }

            if (
              !normalizedQuery
            ) {
              return true;
            }

            return [
              user.name,
              user.email,
              user.phoneNumber,
            ].some(
              (value) =>
                String(
                  value ||
                    ""
                )
                  .toLowerCase()
                  .includes(
                    normalizedQuery
                  )
            );
          }
        );
      },
      [
        users,
        query,
        statusFilter,
      ]
    );

  const openUserModal = (
    user
  ) => {
    const userId =
      user?.id ??
      user?.user_id;

    if (!userId) {
      console.warn(
        "[UsersSettings] user without id in /users/mini:",
        user
      );

      return;
    }

    setSelectedUserId(
      userId
    );

    setIsDetailsModalOpen(
      true
    );
  };

  const closeUserModal =
    () => {
      setIsDetailsModalOpen(
        false
      );

      setSelectedUserId(
        null
      );
    };

  const handleSaved = (
    updatedUser
  ) => {
    const updatedId =
      updatedUser?.id ??
      updatedUser?.user_id;

    if (!updatedId) {
      return;
    }

    if (
      updatedUser.role &&
      updatedUser.role !== role
    ) {
      updateUsersAndCache(
        (
          previousUsers
        ) =>
          previousUsers.filter(
            (user) =>
              (
                user.id ??
                user.user_id
              ) !== updatedId
          )
      );

      return;
    }

    updateUsersAndCache(
      (
        previousUsers
      ) =>
        previousUsers.map(
          (user) =>
            (
              user.id ??
              user.user_id
            ) === updatedId
              ? {
                  ...user,

                  ...normalizeMiniUser(
                    {
                      ...user,
                      ...updatedUser,
                      id:
                        updatedId,
                    }
                  ),
                }
              : user
        )
    );
  };

  const handleCreated = (
    createdUser
  ) => {
    const normalized =
      normalizeMiniUser(
        createdUser
      );

    if (
      !normalized.id ||
      normalized.role !==
        role
    ) {
      return;
    }

    updateUsersAndCache(
      (
        previousUsers
      ) => {
        const exists =
          previousUsers.some(
            (user) =>
              (
                user.id ??
                user.user_id
              ) ===
              normalized.id
          );

        if (exists) {
          return previousUsers.map(
            (user) =>
              (
                user.id ??
                user.user_id
              ) ===
              normalized.id
                ? {
                    ...user,
                    ...normalized,
                  }
                : user
          );
        }

        return [
          ...previousUsers,
          normalized,
        ].sort(
          (
            a,
            b
          ) =>
            String(
              a.name || ""
            ).localeCompare(
              String(
                b.name ||
                  ""
              )
            )
        );
      }
    );
  };

  const handleDeleteUser =
    async (id) => {
      try {
        await axios.delete(
          `/users/${id}`
        );

        updateUsersAndCache(
          (
            previousUsers
          ) =>
            previousUsers.filter(
              (user) =>
                (
                  user.id ??
                  user.user_id
                ) !== id
            )
        );
      } catch (err) {
        console.error(
          "[users] delete error:",
          err
        );
      } finally {
        setConfirming(
          null
        );
      }
    };

  const renderAvatar = (
    user
  ) => {
    if (
      user.profilePictureUrl
    ) {
      return (
        <img
          className="us-avatar"
          decoding="sync"
          src={
            user.profilePictureUrl
          }
          alt={`${user.name || t(
            "labels.user"
          )} ${t(
            "labels.avatar"
          )}`}
        />
      );
    }

    const initials =
      String(
        user.name || ""
      )
        .split(" ")
        .map(
          (part) =>
            part[0]
        )
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

    return (
      <div className="us-avatar us-avatar-fallback">
        {initials || "?"}
      </div>
    );
  };

  const renderRoleLabel =
    (userRole) =>
      t(
        `role_labels.${userRole}`,
        {
          defaultValue: t(
            `roles.${userRole}`,
            {
              defaultValue:
                userRole ||
                "—",
            }
          ),
        }
      );

  const renderStatusLabel =
    (status) => {
      const normalizedStatus =
        STATUS_KEYS.includes(
          status
        )
          ? status
          : "active";

      return t(
        `status_labels.${normalizedStatus}`
      );
    };

  return (
    <div className="users-settings">
      <div className="us-header">
        <div className="us-header-left">
          <h1>
            {t("title")}
          </h1>

          <p>
            {t("subtitle")}
          </p>
        </div>

        <button
          type="button"
          className="us-add-btn"
          onClick={() =>
            setIsCreateModalOpen(
              true
            )
          }
        >
          <FiPlus
            aria-hidden="true"
          />

          <span>
            {t(
              "actions.add_users"
            )}
          </span>
        </button>
      </div>

      <div className="us-toggle-wrap">
        <div
          className="us-toggle"
          role="tablist"
          aria-label={t(
            "aria.roles_tablist"
          )}
          style={{
            ["--count"]:
              roleOptions.length,
          }}
          onKeyDown={(
            event
          ) => {
            if (
              event.key ===
              "ArrowRight"
            ) {
              const nextIndex =
                (
                  activeRoleIndex +
                  1
                ) %
                roleOptions.length;

              setRole(
                roleOptions[
                  nextIndex
                ].value
              );
            }

            if (
              event.key ===
              "ArrowLeft"
            ) {
              const previousIndex =
                (
                  activeRoleIndex -
                  1 +
                  roleOptions.length
                ) %
                roleOptions.length;

              setRole(
                roleOptions[
                  previousIndex
                ].value
              );
            }
          }}
        >
          <div
            className="us-toggle-slider"
            style={{
              ["--i"]:
                activeRoleIndex,
            }}
            aria-hidden="true"
          />

          {roleOptions.map(
            (
              option,
              index
            ) => (
              <button
                key={
                  option.value
                }
                type="button"
                className={`us-toggle-btn ${
                  index ===
                  activeRoleIndex
                    ? "is-active"
                    : ""
                }`}
                onClick={() =>
                  setRole(
                    option.value
                  )
                }
                role="tab"
                aria-selected={
                  index ===
                  activeRoleIndex
                }
                tabIndex={
                  index ===
                  activeRoleIndex
                    ? 0
                    : -1
                }
              >
                {option.label}
              </button>
            )
          )}
        </div>
      </div>

      <div className="us-toolbar">
        <label className="us-search">
          <FiSearch
            aria-hidden="true"
          />

          <input
            type="search"
            value={query}
            onChange={(
              event
            ) =>
              setQuery(
                event.target
                  .value
              )
            }
            placeholder={t(
              "search.placeholder"
            )}
            aria-label={t(
              "aria.search_users"
            )}
          />
        </label>

        <div className="us-result-count">
          {t(
            filteredUsers.length ===
              1
              ? "results.one"
              : "results.other",
            {
              count:
                filteredUsers.length,
            }
          )}
        </div>
      </div>

      <div className="us-content">
        {showLoading &&
          users.length ===
            0 &&
          !error && (
            <div
              className="us-list us-list-skeleton"
              aria-hidden="true"
            >
              {Array.from({
                length: 6,
              }).map(
                (
                  _,
                  index
                ) => (
                  <div
                    key={
                      index
                    }
                    className="us-card us-card-skeleton"
                  >
                    <div className="us-skeleton-avatar" />

                    <div className="us-card-main">
                      <div className="us-skeleton-line us-skeleton-name" />

                      <div className="us-skeleton-line us-skeleton-meta" />

                      <div className="us-skeleton-line us-skeleton-meta us-skeleton-meta-short" />
                    </div>
                  </div>
                )
              )}
            </div>
          )}

        {error &&
          users.length ===
            0 && (
            <div className="us-panel us-error">
              {error}
            </div>
          )}

        {!loading &&
          !error &&
          users.length ===
            0 && (
            <div className="us-panel us-empty">
              {t(
                "status.empty",
                {
                  role:
                    currentRoleLabel,
                }
              )}
            </div>
          )}

        {!error &&
          users.length > 0 &&
          filteredUsers.length ===
            0 && (
            <div className="us-panel us-empty">
              {t(
                "status.no_results"
              )}
            </div>
          )}

        {!error &&
          filteredUsers.length >
            0 && (
            <div className="us-list">
              {filteredUsers.map(
                (
                  user,
                  index
                ) => {
                  const userId =
                    user.id ??
                    user.user_id;

                  const userStatus =
                    STATUS_KEYS.includes(
                      user.status
                    )
                      ? user.status
                      : "active";

                  return (
                    <article
                      key={
                        userId ??
                        `${user.name}-${index}`
                      }
                      className="us-card"
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        openUserModal(
                          user
                        )
                      }
                      onKeyDown={(
                        event
                      ) => {
                        if (
                          event.key ===
                            "Enter" ||
                          event.key ===
                            " "
                        ) {
                          event.preventDefault();

                          openUserModal(
                            user
                          );
                        }
                      }}
                      aria-label={t(
                        "aria.open_user",
                        {
                          name:
                            user.name ||
                            t(
                              "labels.user"
                            ),
                        }
                      )}
                    >
                      <div className="us-card-top">
                        {renderAvatar(
                          user
                        )}

                        <div className="us-card-main">
                          <h3 className="us-card-name">
                            {user.name ||
                              "—"}
                          </h3>

                          <span className="us-role-chip">
                            {renderRoleLabel(
                              user.role
                            )}
                          </span>

                          <div className="us-contact-list">
                            <div className="us-contact-row">
                              <FiPhone
                                aria-hidden="true"
                              />

                              <span>
                                {user.phoneNumber ||
                                  t(
                                    "labels.no_phone"
                                  )}
                              </span>
                            </div>

                            <div className="us-contact-row">
                              <FiMail
                                aria-hidden="true"
                              />

                              <span
                                title={
                                  user.email ||
                                  ""
                                }
                              >
                                {user.email ||
                                  t(
                                    "labels.no_email"
                                  )}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div
                          className="us-card-actions"
                          onClick={(
                            event
                          ) =>
                            event.stopPropagation()
                          }
                        >
                          <button
                            type="button"
                            className="us-icon-btn us-edit-btn"
                            onClick={() =>
                              openUserModal(
                                user
                              )
                            }
                            title={t(
                              "actions.edit_user"
                            )}
                            aria-label={t(
                              "actions.edit_user"
                            )}
                          >
                            <FiEdit2 />
                          </button>

                          <button
                            type="button"
                            className="us-icon-btn us-danger"
                            onClick={() =>
                              setConfirming(
                                {
                                  id:
                                    userId,
                                  name:
                                    user.name,
                                }
                              )
                            }
                            title={t(
                              "actions.delete_user"
                            )}
                            aria-label={t(
                              "actions.delete_user"
                            )}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>

                      <div className="us-card-footer">
                        <span
                          className={`us-status ${
                            userStatus ===
                            "active"
                              ? "is-active"
                              : "is-disabled"
                          }`}
                        >
                          <span className="us-status-dot" />

                          {renderStatusLabel(
                            userStatus
                          )}
                        </span>
                      </div>
                    </article>
                  );
                }
              )}
            </div>
          )}
      </div>

      {isDetailsModalOpen &&
        selectedUserId && (
          <UserDetailsModal
            userId={
              selectedUserId
            }
            onClose={
              closeUserModal
            }
            onSaved={
              handleSaved
            }
          />
        )}

      {isCreateModalOpen && (
        <CreateUserModalPage
          isOpen={
            isCreateModalOpen
          }
          onClose={() =>
            setIsCreateModalOpen(
              false
            )
          }
          onCreated={
            handleCreated
          }
        />
      )}

      {confirming && (
        <div
          className="us-confirm-backdrop"
          role="dialog"
          aria-modal="true"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setConfirming(
                null
              );
            }
          }}
        >
          <div className="us-confirm">
            <h3>
              {t(
                "confirm.title"
              )}
            </h3>

            <p>
              {t(
                "confirm.text",
                {
                  name:
                    confirming.name ||
                    t(
                      "labels.this_user"
                    ),
                }
              )}
            </p>

            <div className="us-confirm-actions">
              <button
                type="button"
                className="us-btn"
                onClick={() =>
                  setConfirming(
                    null
                  )
                }
              >
                {t(
                  "actions.cancel"
                )}
              </button>

              <button
                type="button"
                className="us-btn us-danger-btn"
                onClick={() =>
                  handleDeleteUser(
                    confirming.id
                  )
                }
              >
                {t(
                  "actions.delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}